import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components/macro';
import { DateRange, OnDateRangeChangeProps, Range } from 'react-date-range';
import { SelectedDateRange } from '..';
import isAfter from 'date-fns/isAfter';
import { ModalBody, ModalFooterButtons } from '@televet/kibble-ui/build/components/Modal';
import { TimePicker } from '@televet/kibble-ui/build/components/TimePicker';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Box } from '@televet/kibble-ui/build/chakra';

interface ModalProps {
  selectionRange: SelectedDateRange | null;
  setSelectionRange: (selectionRange: SelectedDateRange) => void;
  onCloseModal: () => void;
}

const MassTextAlertsCustomDateRangeModal = ({
  selectionRange,
  setSelectionRange,
  onCloseModal,
}: ModalProps): JSX.Element => {
  const today = new Date();
  const [isDateValid, setIsDateValid] = useState(true);
  const [isTimeAltered, setIsTimeAltered] = useState(false);
  // Default to today between 12:00 am and 11:59pm
  const [draftSelectionRange, setDraftSelectionRange] = useState<Range>({
    startDate: new Date(today.setHours(0, 0, 0, 0)),
    endDate: new Date(today.setHours(23, 59, 59, 999)),
    key: 'selection',
  });

  const handleOnSubmitButtonClick = useCallback(() => {
    setSelectionRange(draftSelectionRange);
    onCloseModal();
  }, [setSelectionRange, draftSelectionRange, onCloseModal]);

  const addTimeValuesToDate = useCallback(
    ({ startDate, endDate }): { startDate: Date; endDate: Date } => {
      const newStartDate = new Date(startDate);
      const newEndDate = new Date(endDate);

      if (draftSelectionRange?.startDate) {
        newStartDate.setHours(
          draftSelectionRange?.startDate.getHours(),
          draftSelectionRange?.startDate.getMinutes(),
          0,
          0,
        );
      }

      if (draftSelectionRange?.endDate) {
        newEndDate.setHours(draftSelectionRange?.endDate.getHours(), draftSelectionRange?.endDate.getMinutes(), 0, 0);
      }

      return { startDate: newStartDate, endDate: newEndDate };
    },
    [draftSelectionRange],
  );

  const isDateRangeValid = useCallback((selectedDateRange: { startDate: Date; endDate: Date }): boolean => {
    // Date range must be less than 7 days
    const maxDate = new Date(selectedDateRange?.startDate);
    maxDate.setDate(maxDate.getDate() + 6);
    const isGreaterThan7Days = isAfter(selectedDateRange.endDate, maxDate);

    setIsDateValid(!isGreaterThan7Days);
    return !isGreaterThan7Days;
  }, []);

  useEffect(() => {
    if (selectionRange) {
      //  If a range was previously selected set it as draft
      setDraftSelectionRange({
        startDate: selectionRange.startDate,
        endDate: selectionRange.endDate,
        key: 'selection',
      });
    }
  }, [setDraftSelectionRange, selectionRange]);

  const onDateRangeChange = useCallback(
    (range: OnDateRangeChangeProps): void => {
      setIsDateValid(true);

      const { startDate, endDate } = range.selection;

      if (endDate && startDate) {
        if (!isDateRangeValid({ startDate, endDate })) return;

        let alteredDateRange = { startDate, endDate };

        if (isTimeAltered) {
          alteredDateRange = addTimeValuesToDate({ startDate, endDate });
        } else {
          const newStartDate = new Date(startDate);
          const newEndDate = new Date(endDate);

          newStartDate.setHours(0, 0, 0, 0);
          newEndDate.setHours(23, 59, 59, 999);

          alteredDateRange = {
            startDate: newStartDate,
            endDate: newEndDate,
          };
        }

        setDraftSelectionRange({
          ...alteredDateRange,
          key: 'selection',
        });
      }
    },
    [addTimeValuesToDate, isDateRangeValid, isTimeAltered],
  );

  return (
    <>
      <ModalBody>
        <TimesSelector>
          <TimeItem>
            <Text as="p" size="sm">
              Start at
            </Text>
            <Box maxW="90px" mx={2}>
              <TimePicker
                initialTime={draftSelectionRange.startDate || new Date()}
                onTimeChange={(date): void => {
                  setIsTimeAltered(true);
                  setDraftSelectionRange((prevData) => {
                    return {
                      ...prevData,
                      startDate: date,
                    };
                  });
                }}
                inputProps={{ rightElement: undefined, size: 'sm' }}
                placement="bottom"
              />
            </Box>
            <Text as="p" size="sm">
              on:
            </Text>
          </TimeItem>
          <TimeItem>
            <Text as="p" size="sm">
              End at
            </Text>
            <Box maxW="90px" mx={2}>
              <TimePicker
                initialTime={draftSelectionRange.endDate || new Date()}
                onTimeChange={(date): void => {
                  setIsTimeAltered(true);
                  setDraftSelectionRange((prevData) => {
                    return {
                      ...prevData,
                      endDate: date,
                    };
                  });
                }}
                inputProps={{ rightElement: undefined, size: 'sm' }}
                placement="bottom"
              />
            </Box>
            <Text as="p" size="sm">
              on:
            </Text>
          </TimeItem>
        </TimesSelector>
        <DateRangeContainer>
          <DateRange
            editableDateInputs={false}
            months={1}
            showSelectionPreview={false}
            rangeColors={[isDateValid ? 'background.primary' : 'background.danger']}
            fixedHeight={true}
            minDate={new Date()}
            direction="horizontal"
            ranges={[draftSelectionRange]}
            onChange={onDateRangeChange}
          />
          <ErrorMessage isVisible={!isDateValid}>Date range cannot be greater than 7 days</ErrorMessage>
        </DateRangeContainer>
      </ModalBody>
      <ModalFooterButtons isDisabled={!isDateValid} onSubmit={handleOnSubmitButtonClick} />
    </>
  );
};

export default MassTextAlertsCustomDateRangeModal;

const TimesSelector = styled.div`
  padding: 5px 0;
  display: flex;
  width: 100%;
  justify-content: flex-start;
  border-radius: 4px 4px 0 0;
  margin: 0 auto;
  text-align: center;
  background-color: var(--chakra-colors-background-primaryAlpha);
`;

const TimeItem = styled.div`
  align-items: center;
  display: flex;
  flex-basis: 50%;
  justify-content: center;
`;

const DateRangeContainer = styled.div`
  .rdrDateRangeWrapper {
    width: 100%;
    border-radius: 0 0 4px 4px;
  }

  .rdrMonths,
  .rdrMonth {
    width: 100%;
  }

  .rdrDateDisplay {
    margin-top: 0;
  }
  .rdrDayActive {
    .rdrDayNumber {
    }
  }

  .rdrDateDisplayWrapper {
    background-color: var(--chakra-colors-background-primaryAlpha);
  }
`;

const ErrorMessage = styled.div<{ isVisible: boolean }>`
  text-align: center;
  display: ${({ isVisible }): string => (isVisible ? 'block' : 'none')};
  font-size: 12px;
  font-style: italic;
  margin: 10px auto 0;
`;
